import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import logoUrl from '../../assets/logos/zoniq.png';
import { eventsApi } from '../../api';
import { STATE_CITY_MAP, useLocationContext } from '../../context/LocationContext';
import {
  WHEN_OPTIONS,
  compareEventStart,
  formatDayShort,
  formatTime12,
  isUpcomingDate,
  relativeDay,
} from '../../utils/eventDates';
import type { WhenKey } from '../../utils/eventDates';
import { categoryMeta, tally } from '../../utils/eventCategories';
import { announcementsApi } from '../../api/announcementsApi';
import { smoothScrollToElement } from '../../utils/smoothScroll';
import {
  CardSkeletons,
  EventPoster,
  HomeEventCard,
  PromoCard,
  PromoSmokeTile,
  SecHead,
  StateBlock,
  eventMinPrice,
  formatPrice,
} from './HomeParts';
import { Icon } from '../../components/icons/SpriteIcons';
import { ShareButton } from '../../components/events';
import './home.css';

const ALL_CITIES = 'All Cities';

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const { city: activeCity, state: activeState, setCity, setState } = useLocationContext();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['events'],
    queryFn: () => eventsApi.getAllEvents(),
  });

  const [query, setQuery] = useState('');
  const [when, setWhen] = useState<WhenKey>('any');
  const [citySelection, setCitySelection] = useState<string | null>(null);
  const [upcomingCategory, setUpcomingCategory] = useState('all');
  const { data: promos = [] } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => announcementsApi.list(),
    staleTime: 60_000,
  });

  const allUpcoming = useMemo(
    () => (data ?? []).filter((e) => isUpcomingDate(e.date)).sort(compareEventStart),
    [data]
  );
  const scoped = useMemo(
    () => (activeCity === ALL_CITIES ? allUpcoming : allUpcoming.filter((e) => e.city === activeCity)),
    [allUpcoming, activeCity]
  );
  const cities = useMemo(() => tally(allUpcoming.map((e) => e.city)), [allUpcoming]);
  const cats = useMemo(() => tally(scoped.map((e) => e.category)), [scoped]);

  const heroCity = citySelection ?? (activeCity === ALL_CITIES ? 'all' : activeCity);
  const activeUpcomingCategory =
    upcomingCategory !== 'all' && cats.some(([c]) => c === upcomingCategory) ? upcomingCategory : 'all';

  const upcomingList = useMemo(
    () => scoped.filter((e) => activeUpcomingCategory === 'all' || e.category === activeUpcomingCategory).slice(0, 8),
    [scoped, activeUpcomingCategory]
  );
  const featured = useMemo(
    () => [...scoped.filter((e) => e.featured), ...scoped.filter((e) => !e.featured)].slice(0, 3),
    [scoped]
  );
  const fanEvents = scoped.slice(0, 3);
  const nextUp = scoped[0];
  const tickerItems = scoped.slice(0, 8);

  // When a specific city is selected, still surface what's happening elsewhere so
  // a sparse local catalog never fully hides live events (e.g. "1 in Mumbai"
  // while a Chhatarpur event was invisible). Respects the active category filter.
  const otherCitiesUpcoming = useMemo(
    () =>
      activeCity === ALL_CITIES
        ? []
        : allUpcoming
            .filter((e) => e.city !== activeCity)
            .filter((e) => activeUpcomingCategory === 'all' || e.category === activeUpcomingCategory)
            .slice(0, 8),
    [allUpcoming, activeCity, activeUpcomingCategory]
  );
  const otherCount = activeCity === ALL_CITIES ? 0 : allUpcoming.length - scoped.length;

  const applyCity = (name: string) => {
    if (name === 'all' || name === ALL_CITIES) {
      setCity(ALL_CITIES);
      return;
    }
    const stateName = Object.keys(STATE_CITY_MAP).find((s) => STATE_CITY_MAP[s].includes(name));
    if (stateName && stateName !== activeState) setState(stateName);
    setCity(name);
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    applyCity(heroCity);
    const params = new URLSearchParams();
    if (query.trim()) params.set('search', query.trim());
    if (when !== 'any') params.set('when', when);
    const qs = params.toString();
    navigate(qs ? `/events?${qs}` : '/events');
  };
  const goCategory = (slug: string) => navigate(`/events?category=${encodeURIComponent(slug)}`);
  const goCity = (name: string) => {
    applyCity(name);
    navigate('/events');
  };

  const cityLine =
    activeCity !== ALL_CITIES
      ? `in ${activeCity}`
      : cities.length === 1
        ? `in ${cities[0][0]}`
        : `across ${cities.length} cities`;
  const pillText = isLoading
    ? 'Loading live events…'
    : scoped.length
      ? `Live feed · ${scoped.length} upcoming ${cityLine}${otherCount ? ` · ${otherCount} more elsewhere` : ''}`
      : otherCount
        ? `Live feed · ${otherCount} upcoming in other cities`
        : 'Live feed';

  const cityOptions =
    heroCity !== 'all' && !cities.some(([c]) => c === heroCity) ? [...cities, [heroCity, 0] as [string, number]] : cities;

  // Header links like /#home-cities land here; sections only exist once data has loaded.
  useEffect(() => {
    if (!routeLocation.hash) return undefined;
    const id = routeLocation.hash.slice(1);
    let cancelScroll: (() => void) | undefined;
    // Let the page settle (reveal animations, data-driven sections) before gliding to the section.
    const timer = window.setTimeout(() => {
      // The id lives on the section's heading (SecHead puts it on the <h2>), but the eyebrow
      // label sits visually above that heading - landing on the h2 itself clips the eyebrow
      // (and the section's own top padding) off the top of the viewport. Land on the whole
      // <section> instead so the heading comes in with its eyebrow and breathing room intact.
      const el = document.getElementById(id);
      const target = (el?.closest('section') as HTMLElement | null) ?? el;
      cancelScroll = smoothScrollToElement(target);
    }, 250);
    return () => {
      window.clearTimeout(timer);
      cancelScroll?.();
    };
  }, [routeLocation.key, routeLocation.hash, isLoading]);

  const [lead, ...side] = featured;
  // The big tile follows the city picked in the navbar; with no city chosen it falls back to the busiest one.
  const featuredCity = activeCity !== ALL_CITIES ? activeCity : cities[0]?.[0];
  const featuredCount = cities.find(([c]) => c === featuredCity)?.[1] ?? 0;
  const otherCities = cities.filter(([c]) => c !== featuredCity);

  let upcomingBody: React.ReactNode;
  if (isLoading) {
    upcomingBody = (
      <div className="cards-home">
        <CardSkeletons count={4} />
      </div>
    );
  } else if (isError && !data) {
    upcomingBody = (
      <StateBlock
        tone="error"
        icon="i-alert"
        title="We couldn't load events"
        actions={
          <button type="button" className="btn btn-primary" onClick={() => refetch()}>
            <Icon name="i-refresh" />
            Try again
          </button>
        }
      >
        {(error as Error | null)?.message || 'Something went wrong.'} Check your connection and try again.
      </StateBlock>
    );
  } else if (!scoped.length) {
    const cityEmpty = allUpcoming.length > 0 && activeCity !== ALL_CITIES;
    upcomingBody = (
      <StateBlock
        icon="i-compass"
        title={cityEmpty ? `No events in ${activeCity} yet` : 'No upcoming events right now'}
        actions={
          cityEmpty ? (
            <button type="button" className="btn btn-primary" onClick={() => setCity(ALL_CITIES)}>
              Show all cities
            </button>
          ) : (
            <Link className="btn btn-primary" to="/contact">
              List your event
            </Link>
          )
        }
      >
        {cityEmpty
          ? 'Try another city - or see everything that is coming up.'
          : 'New events are added all the time. Check back soon, or tell us what you would like to see.'}
      </StateBlock>
    );
  } else {
    upcomingBody = (
      <div className="cards-home">
        {upcomingList.map((e) => (
          <HomeEventCard key={e.id} event={e} />
        ))}
      </div>
    );
  }

  return (
    <div className="zq-home">
      {/* Hero */}
      <section className="hero wrap" aria-labelledby="zq-hero-h">
        <div className="hero-grid">
          <div className="hero-copy">
            <span className="live-pill">
              <span className="live-dot" />
              {pillText}
            </span>
            <h1 className="h-display" id="zq-hero-h">
              Find your next <span className="accent">experience.</span>
            </h1>
            <p className="lede">Discover events, explore new experiences, and book your next outing with Zoniq.</p>
            <form className="search" role="search" aria-label="Find events" onSubmit={onSearch}>
              <div className="field">
                <label htmlFor="zq-hs-q">What are you looking for?</label>
                <input
                  id="zq-hs-q"
                  type="search"
                  placeholder="Events, artists, venues"
                  autoComplete="off"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="zq-hs-city">Where?</label>
                <select id="zq-hs-city" value={heroCity} onChange={(e) => setCitySelection(e.target.value)}>
                  <option value="all">All cities</option>
                  {cityOptions.map(([c]) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="zq-hs-when">When?</label>
                <select id="zq-hs-when" value={when} onChange={(e) => setWhen(e.target.value as WhenKey)}>
                  {WHEN_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <button className="btn btn-primary go" type="submit">
                <Icon name="i-search" />
                Search
              </button>
            </form>
            {cats.length ? (
              <div className="quick-cats">
                <span className="lbl">Browse</span>
                {cats.slice(0, 5).map(([c]) => (
                  <button key={c} type="button" className="chip" onClick={() => goCategory(c)}>
                    <Icon name={categoryMeta(c).icon} size={14} />
                    {categoryMeta(c).label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {fanEvents.length ? (
            <div className="fan" aria-label="Featured events">
              {fanEvents.map((e) => (
                <Link key={e.id} className="p" to={`/events/${e.slug}`} aria-label={e.title}>
                  <EventPoster event={e} eager />
                </Link>
              ))}
              {nextUp ? (
                <div className="fan-card">
                  <div className="meta">
                    <span className="eyebrow red" style={{ fontSize: 10 }}>
                      Next up · {relativeDay(nextUp.date)}
                    </span>
                    <span className="t">{nextUp.title}</span>
                    <span className="s">
                      {nextUp.venue}
                      {nextUp.city ? `, ${nextUp.city}` : ''}
                      {nextUp.time ? ` · ${formatTime12(nextUp.time)}` : ''}
                    </span>
                  </div>
                  <Link className="btn btn-primary btn-sm" to={`/events/${nextUp.slug}`}>
                    Book
                  </Link>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="fan-empty">
              <img src={logoUrl} alt="Zoniq" />
              <p>{isLoading ? 'Fetching what is on…' : 'Events appear here as soon as they are listed.'}</p>
            </div>
          )}
        </div>
      </section>

      {/* Ticker */}
      {tickerItems.length ? (
        <div className="ticker" aria-hidden="true">
          <div className="ticker-track">
            {[...tickerItems, ...tickerItems, ...(tickerItems.length < 5 ? [...tickerItems, ...tickerItems] : [])].map(
              (e, i) => (
                <span className="ticker-item" key={`${e.id}-${i}`}>
                  <span className="dot" />
                  <b>{e.title}</b>
                  {formatDayShort(e.date)} · {e.city}
                </span>
              )
            )}
          </div>
        </div>
      ) : null}

      {/* Upcoming */}
      <section className="section wrap" id="upcoming" aria-labelledby="zq-up-h">
        <SecHead
          eyebrow="Upcoming"
          title="What's happening next"
          id="zq-up-h"
          right={
            <Link className="link-arrow" to="/events">
              View all events <Icon name="i-arrow" />
            </Link>
          }
        />
        {cats.length > 1 ? (
          <div className="chips scroll" role="toolbar" aria-label="Filter upcoming by category" style={{ marginBottom: 22 }}>
            <button
              type="button"
              className="chip"
              aria-pressed={activeUpcomingCategory === 'all'}
              onClick={() => setUpcomingCategory('all')}
            >
              All <span className="count">{scoped.length}</span>
            </button>
            {cats.map(([c, n]) => (
              <button
                key={c}
                type="button"
                className="chip"
                aria-pressed={activeUpcomingCategory === c}
                onClick={() => setUpcomingCategory(c)}
              >
                {categoryMeta(c).label} <span className="count">{n}</span>
              </button>
            ))}
          </div>
        ) : null}
        {upcomingBody}

        {activeCity !== ALL_CITIES && otherCitiesUpcoming.length > 0 ? (
          <div style={{ marginTop: 'clamp(32px,5vw,56px)' }}>
            <SecHead
              eyebrow="More across India"
              title={`Beyond ${activeCity}`}
              right={
                <button type="button" className="link-arrow" onClick={() => setCity(ALL_CITIES)}>
                  See all cities <Icon name="i-arrow" />
                </button>
              }
            />
            <div className="cards-home">
              {otherCitiesUpcoming.map((e) => (
                <HomeEventCard key={e.id} event={e} />
              ))}
            </div>
          </div>
        ) : null}
      </section>

      {/* Coming soon - advertising posters for events not ticketed on Zoniq yet */}
      {promos.length ? (
        <section className="section wrap" id="coming-soon" aria-labelledby="zq-cs-h" style={{ paddingTop: 0 }}>
          <SecHead
            eyebrow="Coming soon"
            title="On the horizon"
            id="zq-cs-h"
            right={
              <Link className="link-arrow" to="/upcoming">
                See all upcoming <Icon name="i-arrow" />
              </Link>
            }
          />
          <div className="promo-row">
            {promos.slice(0, 3).map((p) => (
              <PromoCard key={p.id} promo={p} />
            ))}
            <PromoSmokeTile promos={promos.length > 3 ? promos.slice(3) : promos} total={promos.length} />
          </div>
        </section>
      ) : null}

      {/* Featured - only worth a section once there is more than a couple of events */}
      {lead && scoped.length >= 3 ? (
        <section className="section wrap" aria-labelledby="zq-ft-h" style={{ paddingTop: 0 }}>
          <SecHead eyebrow="Featured" title="Experiences worth showing up for." id="zq-ft-h" />
          <div className={`feature${side.length ? '' : ' solo'}`}>
            <article className="feature-lead">
              <div className="lead-poster">
                <EventPoster event={lead} />
              </div>
              <div className="copy">
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span className="tag red">Featured</span>
                  <span className="tag">{categoryMeta(lead.category).label}</span>
                </div>
                <h3>{lead.title}</h3>
                <div className="meta-row">
                  <span>
                    <Icon name="i-cal" />
                    {formatDayShort(lead.date)}
                  </span>
                  {lead.time ? (
                    <span>
                      <Icon name="i-clock" />
                      {formatTime12(lead.time)}
                    </span>
                  ) : null}
                  <span>
                    <Icon name="i-pin" />
                    {lead.venue}
                    {lead.city ? `, ${lead.city}` : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                  <Link className="btn btn-primary btn-lg" to={`/events/${lead.slug}`}>
                    Book tickets · from {formatPrice(eventMinPrice(lead))}
                  </Link>
                  <Link className="btn btn-ghost btn-lg" to={`/events/${lead.slug}`}>
                    View event
                  </Link>
                  <ShareButton event={lead} variant="pill" />
                </div>
              </div>
            </article>
            {side.length ? (
              <div className="feature-side">
                {side.map((e) => (
                  <article className="mini" key={e.id}>
                    <div className="media">
                      <EventPoster event={e} />
                    </div>
                    <div className="copy">
                      <div className="flex items-start justify-between gap-2">
                        <span className="eyebrow">{categoryMeta(e.category).label}</span>
                        <ShareButton event={e} variant="icon" />
                      </div>
                      <h3 className="zq-h3">
                        <Link to={`/events/${e.slug}`}>{e.title}</Link>
                      </h3>
                      <p className="muted" style={{ fontSize: 14 }}>
                        {formatDayShort(e.date)}
                        {e.time ? ` · ${formatTime12(e.time)}` : ''}
                        <br />
                        {e.venue}
                        {e.city ? `, ${e.city}` : ''}
                      </p>
                      <div className="foot">
                        <div className="price">
                          <small>From</small>
                          <b className="num">{formatPrice(eventMinPrice(e))}</b>
                        </div>
                        <span className="link-arrow">
                          Book <Icon name="i-arrow" />
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* Categories */}
      {cats.length ? (
        <section className="section tight wrap" aria-labelledby="home-categories">
          <SecHead
            eyebrow="Categories"
            title="Explore by category"
            id="home-categories"
            right={<span className="faint" style={{ fontSize: 13 }}>Categories appear as events are listed</span>}
          />
          <div className="tiles">
            {cats.map(([c, n]) => {
              const meta = categoryMeta(c);
              return (
                <button
                  key={c}
                  type="button"
                  className="tile"
                  style={{ '--tc': meta.tint } as React.CSSProperties}
                  onClick={() => goCategory(c)}
                >
                  <span className="glyph">
                    <Icon name={meta.icon} size={20} />
                  </span>
                  <span style={{ display: 'grid', gap: 2 }}>
                    <span className="tile-h">{meta.label}</span>
                    <span className="n">{n} upcoming</span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Cities */}
      {featuredCity ? (
        <section className="section tight wrap" aria-labelledby="home-cities">
          <SecHead eyebrow="Cities" title="Find events in your city" id="home-cities" />
          <div className="city-grid">
            <button
              type="button"
              className="city-live"
              onClick={() => (featuredCount > 0 ? goCity(featuredCity) : goCity(ALL_CITIES))}
            >
              <span className="live-pill">
                <span className="live-dot" />
                {featuredCount > 0 ? 'Live now' : 'Your city'}
              </span>
              <span style={{ display: 'grid', gap: 10 }}>
                <span className="big">{featuredCity}</span>
                <span className="muted">
                  {featuredCount > 0
                    ? `${featuredCount} upcoming ${featuredCount === 1 ? 'event' : 'events'} · `
                    : 'No events here yet · '}
                  <span className="browse">
                    {featuredCount > 0 ? 'Browse' : 'See all cities'} <Icon name="i-arrow" size={14} />
                  </span>
                </span>
              </span>
            </button>
            <div className="city-ask">
              {otherCities.length ? (
                <>
                  <div className="chips">
                    {otherCities.map(([c, n]) => (
                      <button key={c} type="button" className="chip" onClick={() => goCity(c)}>
                        <Icon name="i-pin" size={14} />
                        {c} <span className="count">{n}</span>
                      </button>
                    ))}
                  </div>
                  <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '4px 0' }} />
                </>
              ) : null}
              <span className="eyebrow">Coming to more cities</span>
              <h3 className="zq-h3">Where should Zoniq go next?</h3>
              <p className="muted" style={{ fontSize: 14.5 }}>
                Tell us your city. We'll use it to decide where to bring events next.
              </p>
              <div>
                <a className="btn btn-ghost" href="mailto:info@zoniq.in?subject=Bring%20Zoniq%20to%20my%20city">
                  <Icon name="i-mail" />
                  Tell us your city
                </a>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* Why Zoniq */}
      <section className="section tight wrap" aria-labelledby="zq-why-h">
        <SecHead eyebrow="Why Zoniq" title="Made for going out, not for scrolling." id="zq-why-h" />
        <div className="why">
          <div>
            <span className="ic">
              <Icon name="i-compass" size={18} />
            </span>
            <h3>Discover more</h3>
            <p>Browse what's on by category, city or date - every upcoming event in one place.</p>
          </div>
          <div>
            <span className="ic">
              <Icon name="i-bolt" size={18} />
            </span>
            <h3>Book in a few taps</h3>
            <p>Pick a ticket tier and how many. Your total is calculated before you check out.</p>
          </div>
          <div>
            <span className="ic">
              <Icon name="i-receipt" size={18} />
            </span>
            <h3>Know what you're paying</h3>
            <p>Every event shows its starting price, and each ticket tier says what's included.</p>
          </div>
          <div>
            <span className="ic">
              <Icon name="i-shield" size={18} />
            </span>
            <h3>Fair access, by design</h3>
            <p>Zoniq is being built so tickets reach real fans - not bots and scalpers.</p>
          </div>
        </div>
      </section>

      {/* Partner band */}
      <section className="section wrap" aria-labelledby="zq-pt-h">
        <div className="partner">
          <div style={{ display: 'grid', gap: 18 }}>
            <span className="eyebrow">For partners</span>
            <h2 className="zq-h1" id="zq-pt-h">
              Let's create something people show up for.
            </h2>
            <p className="lede">
              Organisers, venues, brands and artists - talk to the Zoniq team about listing events, ticketing,
              collaborations and more.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
              <a className="btn btn-primary btn-lg" href="https://event-os.zoniq.in/" target="_blank" rel="noopener noreferrer">
                Partner with Zoniq <Icon name="i-arrow" />
              </a>
              <Link className="btn btn-ghost btn-lg" to="/about">
                How it works
              </Link>
            </div>
          </div>
          <div className="partner-types">
            {[
              ['i-ticket', 'List an event'],
              ['i-hand', 'Ticketing partnership'],
              ['i-star', 'Brand collaboration'],
              ['i-building', 'Venue partnership'],
              ['i-mega', 'Promotions'],
              ['i-mail', 'Something else'],
            ].map(([icon, label]) => (
              <div className="ptype" key={label}>
                <span className="ic">
                  <Icon name={icon} size={16} />
                </span>
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EventOS */}
      <section className="wrap" aria-label="Zoniq ecosystem">
        <div className="eco">
          <div className="l">
            <span className="mk">EVENTOS</span>
            <p>
              <b>Behind every great event is a lot of work.</b> Zoniq also builds EventOS - the back office for event
              teams, from planning to payouts.
            </p>
          </div>
          <a className="link-arrow" href="https://event-os.zoniq.in/" target="_blank" rel="noopener noreferrer">
            Explore EventOS <Icon name="i-arrow" />
          </a>
        </div>
      </section>
    </div>
  );
};
